import _VocabularyDetailQuery from "../__generated__/VocabularyDetailQuery.graphql";
import _VocabularyDetailExamplesQuery from "../__generated__/VocabularyDetailExamplesQuery.graphql";
import _VocabularyDetailMeaningQuery from "../__generated__/VocabularyDetailMeaningQuery.graphql";
import { Loader2, Mic, Volume2 } from 'lucide-react';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Link, useParams } from 'react-router-dom';
import { t } from '../i18n/t';
import { T } from '../i18n/RichText';
import { En } from '../i18n/En';
import { codeFromError, copyFor } from './aidErrors.ts';
import { Speak, TranslationBoundary } from './AidsPanel';
import { registerFor } from '../profile/register';
import type { Register } from '../profile/register';
import { visibleIn } from './practice.ts';
import { pronunciationFor } from './pronunciation.ts';
import { usePractise } from './usePractise';
import { meaningView } from './wordMeaning.ts';
import type { AidsPanelSpeakMutation } from '../__generated__/AidsPanelSpeakMutation.graphql';
import type { VocabularyDetailExamplesQuery } from '../__generated__/VocabularyDetailExamplesQuery.graphql';
import type { VocabularyDetailMeaningQuery } from '../__generated__/VocabularyDetailMeaningQuery.graphql';
import type { VocabularyDetailQuery } from '../__generated__/VocabularyDetailQuery.graphql';

/**
 * What the word means, in a query of its own for the reason `Examples` has
 * one: it can reach a model on first read (word-meanings §3).
 *
 * A failed generation is data (`MeaningFailure`), not an error on the field:
 * async-graphql leaves a failed field's key out, and Relay cannot tell that
 * from a field it never fetched (word-meanings amendment, 2026-09-27).
 * `network-only` so that a failure is asked again on the next open (§9) and a
 * language changed since is picked up; a stored meaning costs the server
 * nothing to return. Not `store-and-network`: a store-first render would show
 * the last open's failure, or a meaning in the old language, until the
 * network answered.
 */
function Meaning({
  itemId
}: {
  itemId: string;
}) {
  const data = useLazyLoadQuery<VocabularyDetailMeaningQuery>((_VocabularyDetailMeaningQuery.hash && _VocabularyDetailMeaningQuery.hash !== "5e15325b8b2e38264241586dfd76eafb" && console.error("The definition of 'VocabularyDetailMeaningQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _VocabularyDetailMeaningQuery), {
    id: itemId
  }, {
    fetchPolicy: 'network-only'
  });
  const view = meaningView(data.node?.meaning, data.me?.learner?.nativeLanguage);
  if (!view) return null;
  if (view.kind === 'failed') {
    return <MeaningSection>
        <p role="alert" className="text-error text-xs">
          {t(view.message)}
        </p>
      </MeaningSection>;
  }
  if (view.kind === 'set-language') {
    return <MeaningSection>
        <p className="text-base-content/60 text-sm">
          <T k="vocab-set-language" parts={{
          link: s => <Link to="/account/profile" className="link">
                  {s}
                </Link>
        }} />
        </p>
      </MeaningSection>;
  }
  return <MeaningSection>
      <p className="text-xl" lang={view.lang}>
        {view.text}
      </p>
    </MeaningSection>;
}
function MeaningSection({
  children
}: {
  children: ReactNode;
}) {
  return <section className="flex flex-col gap-1">
      <h2 className="text-base-content/60 text-sm">{t('vocab-meaning')}</h2>
      {children}
    </section>;
}

/**
 * The sentences to try, in a query of their own.
 *
 * Its own query and its own boundary because it is the one field on this
 * screen that can reach a model: generated on the first read (vocabulary PRD
 * §4), and a failed generation is an error on that field. In the main query it
 * would null the whole word; here it costs only this section.
 */
function Examples({
  itemId
}: {
  itemId: string;
}) {
  const data = useLazyLoadQuery<VocabularyDetailExamplesQuery>((_VocabularyDetailExamplesQuery.hash && _VocabularyDetailExamplesQuery.hash !== "3d75f9664dcae729ab55c7e993bfc8d3" && console.error("The definition of 'VocabularyDetailExamplesQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _VocabularyDetailExamplesQuery), {
    id: itemId
  });
  const sentences = data.node?.exampleSentences ?? [];
  if (sentences.length === 0) return null;
  return <ul className="flex flex-col gap-2">
      {sentences.map(s => <li key={s} className="bg-base-200 rounded p-3">
          <En>{s}</En>
        </li>)}
    </ul>;
}

/**
 * The IPA line under a word or a target form. `lang="en-fonipa"` is the BCP 47
 * tag for IPA; the symbols stay readable by a screen reader rather than being
 * hidden behind a label, since the reader who wants them is the one who asked.
 */
export function Pronunciation({
  ipa,
  register
}: {
  ipa: readonly string[] | null | undefined;
  register: Register;
}) {
  const line = pronunciationFor(ipa, register);
  if (!line) return null;
  return <p className="text-base-content/70 text-sm">
      <span lang="en-fonipa" dir="ltr">
        {line}
      </span>{' '}
      <span className="text-base-content/50 text-xs">{t('pronunciation-us')}</span>
    </p>;
}

/**
 * One word: where it was met, how it sounds, what it means, and sentences to
 * try it in — vocabulary PRD §4.
 *
 * No IPA. It does not exist yet, and §4 says "absent until then, not faked",
 * so there is no empty row and no placeholder. "Practise this" sits beside
 * Hear it, in both registers (`2026-09-27-word-practice-design.md` §7): the
 * word alone reaches the tutor, and `practisable` is the server's own check,
 * so a word it would refuse has no button.
 *
 * Amendment, 2026-09-23: IPA now exists (`2026-09-23-ipa-aid-design.md`) and
 * is drawn under the word by `Pronunciation`, for adults. "Absent, not faked"
 * still holds per word: one CMUdict cannot answer shows nothing.
 */
export function VocabularyDetail() {
  const {
    itemId = ''
  } = useParams<{
    itemId: string;
  }>();
  const data = useLazyLoadQuery<VocabularyDetailQuery>((_VocabularyDetailQuery.hash && _VocabularyDetailQuery.hash !== "2bfa4eabf76136aa81a3b0995dacb1c0" && console.error("The definition of 'VocabularyDetailQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _VocabularyDetailQuery), {
    id: itemId
  });

  // The aid's own speak mutation, addressed by the word's id: the server reads
  // the word from the row, so there is no text here a caller could substitute.
  const [speak, speaking] = useMutation<AidsPanelSpeakMutation>(Speak);
  const [speakError, setSpeakError] = useState<string | null>(null);
  const {
    practise,
    busy: starting,
    why
  } = usePractise();
  // A second tap stops the first clip before starting its own, the
  // arrangement `AidsPanel` makes for the same reason.
  const audio = useRef<HTMLAudioElement | null>(null);
  useEffect(() => () => audio.current?.pause(), []);
  const play = useCallback(() => {
    setSpeakError(null);
    audio.current?.pause();
    speak({
      variables: {
        input: {
          vocabularyItemId: itemId
        }
      },
      onCompleted: ({
        speak: result
      }) => {
        audio.current?.pause();
        const el = new Audio(`data:audio/wav;base64,${result.wavBase64}`);
        audio.current = el;
        void el.play().catch(() => {
          if (audio.current === el) setSpeakError(t(copyFor(null)));
        });
      },
      onError: err => setSpeakError(t(copyFor(codeFromError(err))))
    });
  }, [speak, itemId]);
  const word = data.node && 'word' in data.node ? data.node : null;
  // The list's register gate, applied again here: a child who reaches a
  // corrected word by URL is told it is not in their list, which is what the
  // list itself shows them (vocabulary PRD §8, §11 criterion 3).
  const register = registerFor(data.me?.learner?.defaultCharacter?.audience);
  if (!word?.word || !visibleIn(word.source ?? '', register)) {
    return <div className="flex flex-col gap-4 py-6">
        <p className="text-base-content/60">{t('vocab-missing')}</p>
        <Link to="/practice?surface=words" className="link">
          {t('vocab-back')}
        </Link>
      </div>;
  }
  const metIn = word.metIn;
  return <div className="flex flex-col gap-6 py-6">
      <Link to="/practice?surface=words" className="link text-sm">
        {t('vocab-back-short')}
      </Link>

      <header className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-semibold">
            <En>{word.word}</En>
          </h1>
          <Pronunciation ipa={word.ipa} register={register} />
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className="btn btn-outline min-h-(--hit-min) gap-2" disabled={speaking} onClick={play}>
            {speaking ? <Loader2 aria-hidden className="size-4 animate-spin" /> : <Volume2 aria-hidden className="size-4" />}
            {speaking ? t('common-loading') : t('aids-hear-it')}
          </button>
          {word.practisable && <button type="button" className="btn btn-primary min-h-(--hit-min) gap-2" disabled={starting} onClick={() => practise({
          vocabularyItemId: itemId
        })}>
              <Mic aria-hidden className="size-4" />
              {starting ? t('common-starting') : t('practice-start')}
            </button>}
        </div>
      </header>
      {speakError && <p role="alert" className="text-error text-xs">
          {speakError}
        </p>}
      {why && <p role="alert" className="text-error text-xs">
          {why}
        </p>}

      {/* Above where it was met: a learner should know what a word means
          before rereading the line it came from (word-meanings §12). */}
      <TranslationBoundary>
        <Suspense fallback={<MeaningSection>
              <p className="text-base-content/50 text-sm">{t('vocab-looking-up')}</p>
            </MeaningSection>}>
          <Meaning itemId={itemId} />
        </Suspense>
      </TranslationBoundary>

      {metIn && <section className="flex flex-col gap-1">
          <h2 className="text-base-content/60 text-sm">{t('vocab-met-in')}</h2>
          {/* Left to right as a whole, quotation marks included: left to an
              Arabic page, a wrapped line put the closing mark at the start of
              the next. */}
          <p className="italic">
            <span dir="ltr">{t('common-quoted', {
            text: metIn.transcript
          })}</span>
          </p>
        </section>}

      <section className="flex flex-col gap-2">
        <h2 className="text-base-content/60 text-sm">{t('vocab-examples')}</h2>
        <TranslationBoundary>
          <Suspense fallback={<p className="text-base-content/50 text-sm">{t('vocab-writing-examples')}</p>}>
            <Examples itemId={itemId} />
          </Suspense>
        </TranslationBoundary>
      </section>
    </div>;
}